import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoDirective } from '@jsverse/transloco';
import { homeMaintenanceLogo, homeRenovationLogo, ladderLogo, secure24Logo } from '@ld.consumer-portal/data-uri';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

export type PartnerType = 'home-renovation' | 'home-maintenance' | 'life-insurance' | 'home-security';

export interface PartnerConfig {
  id: PartnerType;
  name: string;
  logo?: string;
  url: string;
  privacyPolicyUrl?: string;
  privacyLinkKey?: string;
  termsUrl?: string;
  termsLinkKey?: string;
  disclaimerFootnoteKey?: string;
  disclaimerTextBeforeKey?: string;
  disclaimerTextAfterKey?: string;
  translationKeyPrefix: string;
  confirmButtonKey: string;
}

export const PARTNER_CONFIGS: Record<PartnerType, PartnerConfig> = {
  'home-renovation': {
    id: 'home-renovation',
    name: 'Bosscat',
    logo: homeRenovationLogo,
    url: 'https://portal.bosscathome.com/mellohome/repairs?utm_source=servicing&utm_medium=website&utm_campaign=landingpage',
    privacyPolicyUrl: 'https://bosscathome.com/privacy/',
    translationKeyPrefix: 'partnersHome-renovation',
    confirmButtonKey: 'commonLearnMore',
  },
  'home-maintenance': {
    id: 'home-maintenance',
    name: 'Bosscat',
    logo: homeMaintenanceLogo,
    url: 'https://portal.bosscathome.com/mellohome/maintenance?utmsource=servicing&utm_medium=website&utm_campaign=landingpage',
    privacyPolicyUrl: 'https://bosscathome.com/privacy/',
    translationKeyPrefix: 'partnersHome-maintenance',
    confirmButtonKey: 'commonLearnMore',
  },
  'life-insurance': {
    id: 'life-insurance',
    name: 'Ladder',
    logo: ladderLogo,
    url: 'https://www.ladderlife.com/loandepot?leadsource=CP_CTA_LifeIns',
    privacyPolicyUrl: 'https://www.ladderlife.com/privacy',
    termsUrl: 'https://www.ladderlife.com/terms',
    termsLinkKey: 'partnersLife-insuranceTermsLinkText',
    disclaimerFootnoteKey: 'partnersLife-insuranceDisclaimerFootnote',
    disclaimerTextBeforeKey: 'partnersLife-insuranceDisclaimerTextBefore',
    disclaimerTextAfterKey: 'partnersLife-insuranceDisclaimerTextAfter',
    translationKeyPrefix: 'partnersLife-insurance',
    confirmButtonKey: 'commonButtonsContinue',
  },
  'home-security': {
    id: 'home-security',
    name: 'Secure24',
    logo: secure24Logo,
    url: 'https://s24alarms.com/loandepotservicing?leadsource=CP_CTA_HomeSecurity',
    privacyPolicyUrl: 'https://www.s24security.com/privacy-policy',
    privacyLinkKey: 'partnersHome-securityPrivacyLinkText',
    disclaimerTextBeforeKey: 'partnersHome-securityDisclaimerTextBefore',
    disclaimerTextAfterKey: 'partnersHome-securityDisclaimerTextAfter',
    translationKeyPrefix: 'partnersHome-security',
    confirmButtonKey: 'commonButtonsContinue',
  },
};

@Component({
  selector: 'ld-partner-disclosure-modal',
  imports: [BaseModalComponent, MatIconModule, TranslocoDirective],
  templateUrl: './partner-disclosure-modal.component.html',
  styleUrl: './partner-disclosure-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerDisclosureModalComponent {
  partnerType = input.required<PartnerType>();

  confirm = output<PartnerConfig>();

  cancel = output<void>();

  protected get config(): PartnerConfig {
    return PARTNER_CONFIGS[this.partnerType()];
  }

  protected onConfirm(): void {
    this.confirm.emit(this.config);
  }

  protected onCancel(): void {
    this.cancel.emit();
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.cancel.emit();
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.cancel.emit();
    }
  }
}
